import { useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { fieldLabel, type ObjectSummary } from '../format';
import type { CellRenderer, Column } from './cells';
import type { Selection } from './selection';
import type { Row } from './useCollection';

/**
 * Table de base (59 §5.2 ; ADR-096) : une représentation, pas un conteneur. Elle rend les lignes que
 * le conteneur lui donne, la sélection qu'il porte et l'ordre de tri qu'il connaît ; elle ne lit
 * jamais l'API. Mode `condensed` : lignes denses, mêmes colonnes. Clavier : flèches, Début / Fin,
 * Entrée ouvre, Espace coche.
 */

export interface RendererProps {
  rows: readonly Row[];
  object: ObjectSummary | undefined;
  columns: readonly Column[];
  render: CellRenderer;
  selectedId: string | null;
  onOpen: (row: Row) => void;
  /** Présente seulement si la vue déclare des actions de masse servies. */
  selection?: Selection;
}

export function TableRenderer({
  rows,
  object,
  columns,
  render,
  selectedId,
  onOpen,
  selection,
  condensed,
  sort,
  sortable,
  onSort,
}: RendererProps & { condensed: boolean; sort: { field: string; direction: 'asc' | 'desc' } | null; sortable: (field: string) => boolean; onSort: (field: string) => void }) {
  const body = useRef<HTMLTableSectionElement | null>(null);
  const onRowKey = (event: ReactKeyboardEvent<HTMLTableRowElement>, row: Row): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onOpen(row);
      return;
    }
    if (event.key === ' ' && selection !== undefined && typeof row.id === 'string') {
      event.preventDefault();
      selection.toggle(row.id);
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Home' && event.key !== 'End') return;
    const all = [...(body.current?.querySelectorAll<HTMLTableRowElement>('tr[data-row]') ?? [])];
    const index = all.indexOf(event.currentTarget);
    const target = event.key === 'Home' ? all[0] : event.key === 'End' ? all[all.length - 1] : all[index + (event.key === 'ArrowDown' ? 1 : -1)];
    if (target === undefined) return;
    event.preventDefault();
    target.focus();
  };
  const ids = rows.map((row) => row.id).filter((id): id is string => typeof id === 'string');
  const pageState = selection === undefined ? 'none' : selection.coverage(ids);

  return (
    <table className={`ay-table${condensed ? ' ay-table--condensed' : ''}`}>
      <thead>
        <tr>
          {selection !== undefined && (
            <th scope="col" className="ay-col--select">
              <input
                type="checkbox"
                aria-label="Sélectionner les lignes chargées"
                checked={pageState === 'all'}
                ref={(input) => {
                  if (input !== null) input.indeterminate = pageState === 'some';
                }}
                onChange={() => (pageState === 'all' ? selection.clear() : selection.add(ids))}
              />
            </th>
          )}
          {columns.map((column) => {
            const direction = sort?.field === column.field ? sort.direction : null;
            return (
              <th key={column.field} scope="col" className={render.classOf(column)} aria-sort={direction === null ? undefined : direction === 'asc' ? 'ascending' : 'descending'}>
                {sortable(column.field) ? (
                  <button type="button" className="ay-table__sort" onClick={() => onSort(column.field)}>
                    {fieldLabel(object, column.field, column.label)} {direction === 'asc' ? '▲' : direction === 'desc' ? '▼' : ''}
                  </button>
                ) : (
                  fieldLabel(object, column.field, column.label)
                )}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody ref={body}>
        {rows.map((row, index) => {
          const id = typeof row.id === 'string' ? row.id : String(index);
          const checked = selection?.has(id) ?? false;
          const classes = [selectedId === id ? 'ay-table__row--selected' : '', checked ? 'ay-table__row--checked' : ''].filter((entry) => entry !== '').join(' ');
          return (
            <tr key={id} data-row data-opens-record className={classes === '' ? undefined : classes} tabIndex={0} aria-selected={selectedId === id} onClick={() => onOpen(row)} onKeyDown={(event) => onRowKey(event, row)}>
              {selection !== undefined && (
                <td className="ay-col--select" onClick={(event) => event.stopPropagation()}>
                  <input type="checkbox" aria-label={`Sélectionner ${render.display(columns[0]?.field ?? 'id', row)}`} checked={checked} onChange={() => undefined} onClick={(event) => selection.pick(id, event)} />
                </td>
              )}
              {columns.map((column) => (
                <td key={column.field} className={render.classOf(column)}>
                  {render.cell(column, row)}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
